import Modal from "react-bootstrap/Modal";
import React, { useContext } from "react";
import _ from "lodash";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { MediaOutletContext } from "../../data/media_outlet_context_management";
import useMediaOutletUpdateApi from "../../data/use_media_outlet_update_api";
import SaveSpinnerButton from "../../../profiles/SaveSpinnerButton";
import ServerErrorAlert from "../../../profiles/ServerErrorAlert";
import VisibilityControl from "../../../profiles/VisibilityControl";
import { mediaOutletHeaderYupSchema } from "../../../../yup_schemas/media_outlet_schema";

const HEADER_ERROR_LABELS = {
  domain: "Domain",
  headline_custom: "Headline",
  name: "Name",
  prestige: "Prestige",
  visibility: "Visibility",
};

// The read API serves prestige as its raw integer, but the write API assigns the Rails enum, which
// only accepts the name. The select therefore carries names and maps back through this table.
const PRESTIGE_OPTIONS = [
  { value: "base", label: "Base", rank: 0 },
  { value: "tier2", label: "Tier 2 - notable", rank: 1 },
  { value: "tier1", label: "Tier 1 - top tier", rank: 2 },
];

const prestigeName = (rank) => PRESTIGE_OPTIONS.find((option) => option.rank === rank)?.value ?? "base";

// A text input renders a missing value as "", so "" and null are the same absence: an untouched
// blank field is not a change (an outlet with no domain must save without a phantom domain edit),
// and a deliberately cleared field is stored as null — "" would fail the server's format checks.
const normalized = (value) => (value === "" || value === undefined ? null : value);

/**
 * Compares two objects and returns a new object with only the properties that have changed.
 * Sending the whole form on every save would re-submit a name the admin never touched, and a rename
 * resets the slug server-side — so an unrelated visibility change would move the outlet's URL.
 */
function getChangedData(originalData, newData) {
  return _.reduce(
    newData,
    (result, value, key) =>
      !_.isEqual(normalized(value), normalized(originalData[key])) ? { ...result, [key]: normalized(value) } : result,
    {},
  );
}

// Header (name, domain, headline) edit body, plus the two curation controls this admin-only surface exists for:
// prestige and visibility. Both are beyond the designed frame — they are what makes an outlet page
// curated rather than merely editable — and neither is reachable by a non-admin, since the whole edit
// surface is gated on the outlet policy's admin-only #update?.
function HeaderModalBody({ close }) {
  const context = useContext(MediaOutletContext);
  const { mediaOutlet, isUpdating, generalError } = context;
  const { updateMediaOutlet } = useMediaOutletUpdateApi({ ...context });

  const currentPrestige = prestigeName(mediaOutlet.prestige);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(mediaOutletHeaderYupSchema),
  });

  const onSubmit = async (data, event) => {
    event.preventDefault();
    const original = {
      domain: mediaOutlet.domain,
      // The read API serves headline_custom under the name `headline`.
      headline_custom: mediaOutlet.headline,
      name: mediaOutlet.name,
      prestige: currentPrestige,
      visibility: mediaOutlet.visibility,
    };
    // updateMediaOutlet closes the modal and silently reloads the page data on success.
    await updateMediaOutlet(getChangedData(original, data));
  };

  return (
    <>
      <Modal.Body>
        <ServerErrorAlert generalError={generalError} labelMap={HEADER_ERROR_LABELS} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-3 row">
            <label htmlFor="name" className="col-sm-3 col-form-label">
              Name
            </label>
            <div className="col-sm-9">
              <input
                id="name"
                type="text"
                className="form-control"
                {...register("name")}
                defaultValue={mediaOutlet.name}
              />
              {errors?.name && <span className="small text-danger">{errors?.name?.message}</span>}
            </div>
          </div>
          <div className="mb-3 row">
            <label htmlFor="domain" className="col-sm-3 col-form-label">
              Domain
            </label>
            <div className="col-sm-9">
              <input
                id="domain"
                type="text"
                className="form-control"
                placeholder="example.com"
                {...register("domain")}
                defaultValue={mediaOutlet.domain}
              />
              {errors?.domain && <span className="small text-danger">{errors?.domain?.message}</span>}
            </div>
          </div>
          <div className="mb-3 row">
            <label htmlFor="headline_custom" className="col-sm-3 col-form-label">
              Headline
            </label>
            <div className="col-sm-9">
              <input
                id="headline_custom"
                type="text"
                className="form-control"
                placeholder="Catchy headline to display under the outlet name."
                {...register("headline_custom")}
                defaultValue={mediaOutlet.headline}
              />
              {errors?.headline_custom && <span className="small text-danger">{errors?.headline_custom?.message}</span>}
            </div>
          </div>
          <div className="mb-3 row">
            <label htmlFor="prestige" className="col-sm-3 col-form-label">
              Prestige
            </label>
            <div className="col-sm-9">
              <select className="form-select" id="prestige" {...register("prestige")} defaultValue={currentPrestige}>
                {PRESTIGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-3 row">
            <span className="col-sm-3 col-form-label">Visibility</span>
            <div className="col-sm-9">
              <Controller
                name="visibility"
                control={control}
                defaultValue={mediaOutlet.visibility}
                render={({ field }) => <VisibilityControl value={field.value} onChange={field.onChange} />}
              />
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <SaveSpinnerButton
          inactiveText="Save Changes"
          activeText="Saving..."
          isSaving={isUpdating}
          className="btn btn-primary"
          onClick={handleSubmit(onSubmit)}
        />
        <button type="button" className="btn btn-secondary" onClick={close}>
          Close
        </button>
      </Modal.Footer>
    </>
  );
}

export default HeaderModalBody;
